import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Package, Upload, Download, Search, Bell, Plus, Trash2, Clock,
  Swords, ChevronDown, ChevronRight, ArrowRight, LayoutGrid, Table as TableIcon,
  ChevronLeft,
} from "lucide-react";
import ProductPricePanel from "@/components/v2/ProductPricePanel";
import CatalogAskWidget from "@/components/v2/CatalogAskWidget";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

function AddProductDialog({ open, onOpenChange, onDone }) {
  const emptyProduct = { code: "", name: "", category: "", description: "" };
  const emptySku = { part_number: "", name: "", uom: "NOS", hsn_code: "", base_price_inr: "", list_price_inr: "", gst_rate_pct: "18", supplier_name: "" };
  const [product, setProduct] = useState(emptyProduct);
  const [sku, setSku] = useState(emptySku);
  const [addSku, setAddSku] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setProduct(emptyProduct); setSku(emptySku); setAddSku(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    if (!product.code.trim() || !product.name.trim()) {
      toast.error("Code and name are required"); return;
    }
    setBusy(true);
    try {
      const created = await v2Api.catalog.createProduct({
        code: product.code.trim(),
        name: product.name.trim(),
        category: product.category.trim() || null,
        description: product.description.trim() || null,
      });
      if (addSku && sku.part_number.trim() && sku.name.trim()) {
        await v2Api.catalog.createSku(created.id, {
          part_number: sku.part_number.trim(),
          name: sku.name.trim(),
          uom: sku.uom.trim() || "NOS",
          hsn_code: sku.hsn_code.trim() || null,
          base_price_inr: sku.base_price_inr ? parseFloat(sku.base_price_inr) : null,
          list_price_inr: sku.list_price_inr ? parseFloat(sku.list_price_inr) : null,
          gst_rate_pct: parseFloat(sku.gst_rate_pct) || 18,
          supplier_name: sku.supplier_name.trim() || null,
        });
      }
      toast.success(`Product ${created.code} created`);
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Create failed");
    } finally { setBusy(false); }
  };

  const f = (field) => (e) => setProduct(p => ({ ...p, [field]: e.target.value }));
  const sf = (field) => (e) => setSku(p => ({ ...p, [field]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add product</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 block mb-1">Product code *</label>
              <Input placeholder="e.g. CO2-NDIR" value={product.code} onChange={f("code")} />
            </div>
            <div>
              <label className="text-xs text-slate-500 block mb-1">Category</label>
              <Input placeholder="e.g. CEMS, WQM" value={product.category} onChange={f("category")} />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Name *</label>
            <Input placeholder="e.g. CO2 Analyser (NDIR)" value={product.name} onChange={f("name")} />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1">Description</label>
            <Input placeholder="Optional short description" value={product.description} onChange={f("description")} />
          </div>

          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pt-1">
            <input type="checkbox" checked={addSku} onChange={(e) => setAddSku(e.target.checked)} className="rounded" />
            Also add a SKU
          </label>

          {addSku && (
            <div className="border rounded-md p-3 space-y-2 bg-slate-50">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Part number *</label>
                  <Input placeholder="e.g. AXI-CO2-100" value={sku.part_number} onChange={sf("part_number")} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">UOM</label>
                  <Input placeholder="NOS" value={sku.uom} onChange={sf("uom")} />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">SKU name *</label>
                <Input placeholder="e.g. CO2 Analyser NDIR 0-10ppm" value={sku.name} onChange={sf("name")} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Base price (₹)</label>
                  <Input type="number" placeholder="0" value={sku.base_price_inr} onChange={sf("base_price_inr")} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">List price (₹)</label>
                  <Input type="number" placeholder="0" value={sku.list_price_inr} onChange={sf("list_price_inr")} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">HSN code</label>
                  <Input placeholder="e.g. 90278090" value={sku.hsn_code} onChange={sf("hsn_code")} />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">GST %</label>
                  <Input type="number" placeholder="18" value={sku.gst_rate_pct} onChange={sf("gst_rate_pct")} />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Supplier</label>
                <Input placeholder="Optional" value={sku.supplier_name} onChange={sf("supplier_name")} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CatalogImportDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  const downloadTemplate = async () => {
    try {
      const tpl = await v2Api.imports.catalogTemplate();
      const cols = tpl.columns;
      const example = tpl.example || {};
      const csv = [
        cols.join(","),
        cols.map((c) => {
          const v = (example[c] ?? "").toString().replace(/"/g, '""');
          return /[",\n]/.test(v) ? `"${v}"` : v;
        }).join(","),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "catalog-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Template download failed");
    }
  };

  const upload = async () => {
    if (!file) { toast.error("Pick a CSV/XLSX first"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await v2Api.imports.catalogUpload(file);
      setResult(r);
      toast.success(`${r.created} created · ${r.updated} updated · ${r.skipped} skipped`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Bulk import catalog (products + SKUs + rates)</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <p className="text-slate-600">
            One file with a row per SKU. Products are upserted by{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">product_code</code>;
            SKUs are upserted by{" "}
            <code className="text-xs bg-slate-100 px-1 rounded">part_number</code>.
            Includes HSN, UOM, base price, list price, GST %, lead time, supplier.
          </p>
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-1" /> Download template (CSV)
          </Button>
          <Input
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
          {result && (
            <div className="text-xs bg-slate-50 border rounded p-2 space-y-1">
              <p>
                <strong>{result.created}</strong> created ·{" "}
                <strong>{result.updated}</strong> updated ·{" "}
                <strong>{result.skipped}</strong> skipped
              </p>
              {result.errors?.length > 0 && (
                <ul className="list-disc list-inside text-rose-600 max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                  {result.errors.length > 20 && <li>… +{result.errors.length - 20} more</li>}
                </ul>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={upload} disabled={busy || !file}>
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReminderRow({ r, users, onChange }) {
  const assignee = users.find((u) => u.id === r.assignee_user_id);
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b last:border-b-0 text-sm">
      <div className="min-w-0 flex-1">
        <p className="font-medium text-slate-800 truncate">
          <Bell className="w-3.5 h-3.5 inline mr-1 text-amber-500" />
          {r.category ? `Category — ${r.category}` : "Overall rate list"}
        </p>
        <p className="text-xs text-slate-500">
          Day {r.day_of_month} of every month · {assignee?.full_name || assignee?.email || "unknown"}
          {r.last_fired_at ? ` · last fired ${r.last_fired_at}` : ""}
          {" · next "}<span className="font-mono">{r.next_due_at}</span>
        </p>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onChange("snooze", r)}>
        <Clock className="w-3.5 h-3.5 mr-1" /> +7d
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onChange("delete", r)}>
        <Trash2 className="w-3.5 h-3.5 text-rose-500" />
      </Button>
    </div>
  );
}

function NewReminderDialog({ open, onOpenChange, users, onSaved }) {
  const [form, setForm] = useState({ category: "", assignee_user_id: "", day_of_month: 1, note: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ category: "", assignee_user_id: users[0]?.id || "", day_of_month: 1, note: "" });
  }, [open, users]);

  const submit = async () => {
    if (!form.assignee_user_id) { toast.error("Pick an assignee"); return; }
    setBusy(true);
    try {
      await v2Api.reminders.create({
        category: form.category.trim() || null,
        assignee_user_id: form.assignee_user_id,
        day_of_month: Number(form.day_of_month) || 1,
        note: form.note.trim() || null,
      });
      toast.success("Reminder scheduled");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New rate refresh reminder</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div>
            <label className="text-xs text-slate-500">Category (leave blank for overall)</label>
            <Input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="e.g. WQM, CEMS, DAS — or empty for whole catalog"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Assignee</label>
            <select
              value={form.assignee_user_id}
              onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
              className="w-full px-2 py-1 border rounded-md bg-white text-sm"
            >
              <option value="">— pick a user —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Day of month (1–28)</label>
            <Input
              type="number" min="1" max="28"
              value={form.day_of_month}
              onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Note (optional)</label>
            <Input
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="Shown in the notification body"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy}>{busy ? "Saving…" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductRow({ p }) {
  const [expanded, setExpanded] = useState(false);
  const [skus, setSkus] = useState([]);
  const [openSkuId, setOpenSkuId] = useState(null);
  const [loadingSkus, setLoadingSkus] = useState(false);

  const loadSkus = async () => {
    if (skus.length > 0) return;
    setLoadingSkus(true);
    try {
      const data = await v2Api.catalog.skus(p.id);
      setSkus(data || []);
    } catch {
      toast.error("Could not load SKUs");
    } finally { setLoadingSkus(false); }
  };

  const toggle = async () => {
    if (!expanded) await loadSkus();
    setExpanded(!expanded);
    setOpenSkuId(null);
  };

  return (
    <Card>
      <CardContent className="py-3">
        <div className="flex items-center justify-between gap-2 w-full">
          <button onClick={toggle} className="flex items-center gap-2 min-w-0 text-left flex-1">
            {expanded
              ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
              : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
            <div className="min-w-0">
              <p className="font-medium text-slate-800 truncate">
                {p.name}{" "}
                <span className="text-xs text-slate-400 font-mono">({p.code})</span>
              </p>
              <p className="text-xs text-slate-500">{p.category}</p>
            </div>
          </button>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline">
              {p.sku_count ?? 0} SKU{(p.sku_count ?? 0) === 1 ? "" : "s"}
            </Badge>
            <Link
              to={`/v2/catalog/${p.id}`}
              className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
            >
              Details <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>

        {expanded && (
          <div className="mt-3 border-t pt-3 space-y-1">
            {loadingSkus && <p className="text-xs text-slate-400">Loading SKUs…</p>}
            {!loadingSkus && skus.length === 0 && (
              <p className="text-xs italic text-slate-400">
                No SKUs under this product yet. Add one via Bulk import.
              </p>
            )}
            {skus.map((s) => (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => setOpenSkuId(openSkuId === s.id ? null : s.id)}
                  className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded border ${
                    openSkuId === s.id
                      ? "bg-violet-50 border-violet-200"
                      : "bg-white border-slate-200 hover:border-blue-200 hover:bg-slate-50"
                  }`}
                >
                  <div className="min-w-0">
                    <p className="text-sm text-slate-800 truncate">
                      <span className="font-mono text-xs text-blue-700">{s.part_number}</span>
                      {" · "}{s.name}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {s.uom}
                      {s.base_price_inr != null && (
                        <> · base ₹{new Intl.NumberFormat("en-IN", {
                          maximumFractionDigits: 0,
                        }).format(s.base_price_inr)}</>
                      )}
                      {s.list_price_inr != null && (
                        <> · list ₹{new Intl.NumberFormat("en-IN", {
                          maximumFractionDigits: 0,
                        }).format(s.list_price_inr)}</>
                      )}
                    </p>
                  </div>
                  <span className={`text-[10px] flex items-center gap-1 ${
                    openSkuId === s.id ? "text-violet-700" : "text-slate-400"
                  }`}>
                    <Swords className="w-3 h-3" />
                    {openSkuId === s.id ? "Hide prices" : "Competitor prices"}
                  </span>
                </button>
                {openSkuId === s.id && (
                  <ProductPricePanel
                    sku={s}
                    productCategory={p.category}
                    onClosed={() => setOpenSkuId(null)}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CatalogTableRow({ p, onClick }) {
  return (
    <tr
      onClick={onClick}
      className="border-b hover:bg-blue-50/40 cursor-pointer transition-colors"
    >
      <td className="px-3 py-2.5 font-mono text-xs text-slate-500">{p.code || "—"}</td>
      <td className="px-3 py-2.5">
        <div className="font-medium text-slate-900">{p.name}</div>
        {p.description && (
          <div className="text-xs text-slate-500 truncate max-w-md">{p.description}</div>
        )}
      </td>
      <td className="px-3 py-2.5">
        {p.category ? (
          <Badge variant="outline" className="text-[10px] capitalize">{p.category}</Badge>
        ) : <span className="text-slate-300 text-xs">—</span>}
      </td>
      <td className="px-3 py-2.5 text-right">
        <ArrowRight className="w-3.5 h-3.5 text-slate-400 inline" />
      </td>
    </tr>
  );
}

function CatalogCard({ p, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-blue-400 hover:shadow-md transition-all duration-150 flex flex-col gap-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-slate-900 truncate">{p.name}</div>
          <div className="font-mono text-[11px] text-slate-500 mt-0.5">{p.code || "—"}</div>
        </div>
        {p.category && (
          <Badge variant="outline" className="text-[10px] capitalize shrink-0">
            {p.category}
          </Badge>
        )}
      </div>
      {p.description && (
        <p className="text-xs text-slate-600 line-clamp-2">{p.description}</p>
      )}
      <div className="flex items-center justify-end pt-2 border-t mt-auto text-xs">
        <span className="text-blue-600 font-medium inline-flex items-center gap-1">
          View <ArrowRight className="w-3 h-3" />
        </span>
      </div>
    </button>
  );
}

export default function CatalogPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openImport, setOpenImport] = useState(false);
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [openReminder, setOpenReminder] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [users, setUsers] = useState([]);

  // Filter state (persisted across reloads)
  const [q, setQ] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("catalog.cats") || "[]");
    } catch { return []; }
  });
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("catalog.viewMode") || "grid"
  );
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 12;

  const refresh = async () => {
    setLoading(true);
    try { setProducts(await v2Api.catalog.products() || []); }
    catch { toast.error("Could not load catalog"); }
    finally { setLoading(false); }
  };
  const refreshReminders = async () => {
    try { setReminders(await v2Api.reminders.list() || []); } catch { /* ignore */ }
  };
  useEffect(() => {
    refresh();
    refreshReminders();
    v2Api.users().then((u) => setUsers(u || [])).catch(() => {});
  }, []);

  // Persist filter prefs whenever they change so reload preserves context.
  useEffect(() => {
    localStorage.setItem("catalog.cats", JSON.stringify(selectedCategories));
  }, [selectedCategories]);
  useEffect(() => { localStorage.setItem("catalog.viewMode", viewMode); }, [viewMode]);

  const reminderAction = async (action, r) => {
    try {
      if (action === "delete") {
        if (!await confirm("Delete this reminder?", { title: "Delete reminder", confirmLabel: "Delete" })) return;
        await v2Api.reminders.remove(r.id);
      } else if (action === "snooze") {
        await v2Api.reminders.snooze(r.id, 7);
      }
      refreshReminders();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Action failed");
    }
  };

  const categories = useMemo(() => Array.from(
    new Set(products.map((p) => (p.category || "").trim()).filter(Boolean))
  ).sort(), [products]);

  const toggleCategory = (c) => {
    setSelectedCategories((prev) =>
      prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]
    );
    setPage(0);
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (selectedCategories.length > 0
          && !selectedCategories.includes((p.category || "").trim())) return false;
      if (needle) {
        return (
          (p.code || "").toLowerCase().includes(needle) ||
          (p.name || "").toLowerCase().includes(needle) ||
          (p.category || "").toLowerCase().includes(needle) ||
          (p.description || "").toLowerCase().includes(needle)
        );
      }
      return true;
    });
  }, [products, q, selectedCategories]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  const clearAllFilters = () => {
    setQ("");
    setSelectedCategories([]);
    setPage(0);
  };

  const goToDetail = (id) => navigate(`/v2/catalog/${id}`);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="px-6 py-4 max-w-[1500px] mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" /> Product Catalog
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Master list of products, SKUs, certifications &amp; brochures — drives BOQ matching &amp; quotation generation.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpenReminder(true)}
              title="Schedule a monthly nudge to re-quote stale catalog rates"
            >
              <Bell className="w-4 h-4 mr-1" /> Rate Refresh Reminder
            </Button>
            <Button size="sm" variant="outline" onClick={() => setOpenAddProduct(true)}>
              <Plus className="w-4 h-4 mr-1" /> Add product
            </Button>
            <Button size="sm" onClick={() => setOpenImport(true)}>
              <Upload className="w-4 h-4 mr-1" /> Bulk import
            </Button>
          </div>
        </div>
      </div>

      {/* Reminders strip (only when non-empty) */}
      {reminders.length > 0 && (
        <div className="px-6 pt-4 max-w-[1500px] mx-auto">
          <Card>
            <CardContent className="py-3">
              <p className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-2">
                Rate refresh reminders
              </p>
              {reminders.map((r) => (
                <ReminderRow key={r.id} r={r} users={users} onChange={reminderAction} />
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Two-pane layout: filter sidebar + content */}
      <div className="px-6 py-4 max-w-[1500px] mx-auto flex gap-4">
        {/* ---- Filter sidebar ---- */}
        <aside className="w-64 shrink-0 space-y-4">
          <Card>
            <CardContent className="py-3 px-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase text-slate-500 tracking-wider">
                  Filters
                </span>
                <button
                  type="button"
                  className="text-[11px] text-blue-600 hover:underline"
                  onClick={clearAllFilters}
                >
                  Clear
                </button>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium block mb-1.5">
                  Search
                </label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
                  <Input
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setPage(0); }}
                    placeholder="Code, name, category…"
                    className="pl-7 h-8 text-xs"
                  />
                </div>
              </div>

              {categories.length > 0 && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-400 font-medium block mb-1.5">
                    Categories
                    {selectedCategories.length > 0 && (
                      <span className="ml-1 text-blue-600">({selectedCategories.length})</span>
                    )}
                  </label>
                  <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
                    {categories.map((c) => (
                      <label
                        key={c}
                        className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(c)}
                          onChange={() => toggleCategory(c)}
                          className="rounded"
                        />
                        <span className="capitalize truncate">{c}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </aside>

        {/* ---- Content ---- */}
        <main className="flex-1 min-w-0 space-y-3">
          {/* Stats + view toggle */}
          <Card>
            <CardContent className="py-2.5 px-4 flex items-center justify-between flex-wrap gap-2 text-xs">
              <div className="flex gap-5 text-slate-600">
                <span><strong className="text-slate-900">{products.length}</strong> products</span>
                <span><strong className="text-slate-900">{categories.length}</strong> categories</span>
                <span>
                  Showing <strong className="text-slate-900">{filtered.length}</strong>
                  {filtered.length !== products.length && " filtered"}
                </span>
              </div>
              <div className="inline-flex rounded-md border bg-white p-0.5">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  title="Card grid"
                  className={`px-2 py-1 rounded inline-flex items-center gap-1 text-[11px] ${
                    viewMode === "grid" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <LayoutGrid className="w-3 h-3" /> Cards
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  title="Data table"
                  className={`px-2 py-1 rounded inline-flex items-center gap-1 text-[11px] ${
                    viewMode === "table" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <TableIcon className="w-3 h-3" /> Table
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Empty / loading states */}
          {loading && (
            <p className="text-sm text-slate-500 py-10 text-center">Loading…</p>
          )}
          {!loading && filtered.length === 0 && (
            <Card>
              <CardContent className="py-10 text-center text-slate-400">
                {products.length === 0
                  ? <>No products yet. Click <em>Bulk import</em> to upload your rate list.</>
                  : "No products match the current filters."}
              </CardContent>
            </Card>
          )}

          {/* Card grid */}
          {!loading && filtered.length > 0 && viewMode === "grid" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {pageRows.map((p) => (
                <CatalogCard key={p.id} p={p} onClick={() => goToDetail(p.id)} />
              ))}
            </div>
          )}

          {/* Data table */}
          {!loading && filtered.length > 0 && viewMode === "table" && (
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Code</th>
                      <th className="px-3 py-2 text-left font-semibold">Name</th>
                      <th className="px-3 py-2 text-left font-semibold">Category</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((p) => (
                      <CatalogTableRow key={p.id} p={p} onClick={() => goToDetail(p.id)} />
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {!loading && filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-2 text-xs text-slate-600">
              <span>
                Page {safePage + 1} of {totalPages} —{" "}
                rows {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(0, safePage - 1))}
                  disabled={safePage === 0}
                >
                  <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
                  disabled={safePage >= totalPages - 1}
                >
                  Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>

      <AddProductDialog
        open={openAddProduct}
        onOpenChange={setOpenAddProduct}
        onDone={refresh}
      />
      <CatalogImportDialog
        open={openImport}
        onOpenChange={setOpenImport}
        onDone={refresh}
      />
      <NewReminderDialog
        open={openReminder}
        onOpenChange={setOpenReminder}
        users={users}
        onSaved={refreshReminders}
      />

      {/* Floating "Ask the catalog" chat (Phase 2 of the rework).
          Talks to POST /api/v2/catalog/ask with text-only retrieval
          over product name / description / attributes / certs. */}
      <CatalogAskWidget
        open={askOpen}
        onOpen={() => setAskOpen(true)}
        onClose={() => setAskOpen(false)}
      />
    </div>
  );
}
